import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Users, Code, ArrowRight } from 'lucide-react';

const CustomSolutionsSection = () => {
  const services = [
    {
      icon: <Code size={24} />,
      title: 'Custom Development',
      description: 'Tailored software solutions built specifically for your organization\'s unique workflows and challenges.'
    },
    {
      icon: <Users size={24} />,
      title: 'Consulting Services',
      description: 'Strategic guidance and implementation support to help you leverage technology for athlete management, NIL compliance, recruiting operations, and more.'
    },
    {
      icon: <Lightbulb size={24} />,
      title: 'Innovation Partnership',
      description: 'Collaborate with us to bring your sports technology vision to life—from concept to launch.'
    }
  ];

  return (
    <section className="section relative overflow-hidden bg-[#0A0A0A]">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#004534]/20 rounded-full blur-[150px] pointer-events-none" />
      </div>

      <div className="container relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#D3FF62]/30 bg-[#D3FF62]/10 mb-6"
            >
              <Lightbulb size={16} className="text-[#D3FF62]" />
              <span className="text-[#D3FF62] text-sm font-medium">Beyond Our Products</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="display-large text-white mb-6"
            >
              DON'T SEE WHAT<br />
              <span className="gradient-text">YOU NEED?</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="body-large text-white/70 max-w-2xl mx-auto"
            >
              Every sports organization is unique. We partner with teams, agencies, schools, 
              and athletic programs to build custom solutions tailored to your specific needs.
            </motion.p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-[#141414] border border-white/10 hover:border-[#D3FF62]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#D3FF62]/10 flex items-center justify-center mb-5 text-[#D3FF62] group-hover:bg-[#D3FF62]/20 transition-colors">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-white/60 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <a
              href="#contact"
              className="btn-primary inline-flex text-lg px-8 py-4"
            >
              Let's Talk About Your Project
              <ArrowRight size={20} />
            </a>
            <p className="text-white/50 text-sm mt-4">
              No commitment required—just a conversation about what's possible.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CustomSolutionsSection;
